import React, { ReactNode, useCallback, useEffect, useState } from 'react'
import { Know, KnowFormat } from 'interfaces/Know'
import Link from 'next/link'
import styled, { css } from 'styled-components'
import KnowService from 'services/KnowService'
import { ApiError } from 'services/ApiService'
import { ErrorTypes } from 'lib/constants/ErrorTypes'
import { trackPromise } from 'react-promise-tracker'
import LoadingArea from 'components/elements/LoadingArea'
import theme from 'stories/utils/theme'
import { Body1, Body2, ButtonText } from 'stories/elements/Typography/Text'
import KnowLikes from 'assets/icons/webapp_liked.svg'
import KnowViews from 'assets/icons/webapp_views.svg'
import KnowComment from 'assets/icons/webapp_comment.svg'
import KnowFlashcardCount from 'assets/icons/webapp_flashcard_count.svg'
import { toast } from 'react-toastify'
import PDFIcon from 'assets/icons/pdf.svg'
import { knowRoute } from 'lib/constants/routes'
import _useTranslation from 'lib/hooks/_useTranslation'
import { useRouter } from 'next/router'
import { isKnowNotIndexable } from 'lib/features/Know/knowSSR'
import Stack from 'components/elements/Stack'
import ProfilePicture from 'components/elements/ProfilePicture'
import FlashcardInfo from './FlashcardInfo'
import KnowImpressionTracking from 'lib/tracking/KnowImpressionTracking'
import { FeedEventOptions, SearchOptions } from 'interfaces/TrackingSnippets'
import { storeFeedOption, storeSearchOption } from 'lib/tracking/storage'
import { formatSubjectLabel } from 'lib/formatting/subjects'
import useMedia from 'lib/hooks/useMedia'
import { deviceSize } from 'lib/constants/GridBreakpoints'
import MatchingContentIcon from 'assets/app/knows/matching-content-icon.svg'

const Container = styled.div<{ disableLinking?: boolean; isDarkTheme?: boolean }>`
    width: 100%;
    height: auto;
    min-height: 300px;
    display: grid;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    background-color: ${({ isDarkTheme }) => (isDarkTheme ? theme.colors.backgroundTransparent : theme.colors.white)};
    gap: 10px;
    position: relative;
    cursor: pointer;

    ${({ disableLinking, isDarkTheme }) =>
        isDarkTheme
            ? css`
                  :hover {
                      background-color: ${theme.colors.grayScalePrimaryTransparent};
                  }
              `
            : !disableLinking
            ? css`
                  cursor: pointer;
                  :hover {
                      .thumbnail {
                          border-bottom: 4px solid ${theme.colors.deepSeaBlue};
                      }
                  }
              `
            : ''}
    ${({ isDarkTheme }) =>
        isDarkTheme &&
        css`
            padding: 17.5px;
            border-radius: ${theme.borderRadius.webappCard};
        `}

    .profile {
        position: absolute;
        right: 65px;
        top: 165px;
        display: grid;

        place-content: center;
        grid-gap: 10px;
        border-radius: 50%;
    }

    .bottom {
        padding: ${({ isDarkTheme }) => (isDarkTheme ? '5px 2px' : '5px 15px')};
        display: grid;
        grid-gap: 5px;
        row-gap: 15px;
        .stats {
            display: flex;
            gap: 8px;
            padding-bottom: 5px;
            .matching-content-icon {
                background: radial-gradient(109.73% 132.63% at -9.73% 0%, #5ed062 0%, #4ba5ff 100%);
                border-radius: ${theme.borderRadius.webappCard};
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 5px;
                padding: 0 10px;
                width: fit-content;
                svg {
                    position: relative;
                    bottom: 1.5px;
                }
            }
            .icon {
                display: grid;
                align-items: center;
                justify-content: center;
                grid-template-columns: 12px auto;
                gap: 5px;
                border: 1px solid ${({ isDarkTheme }) => (isDarkTheme ? theme.colors.transparent : theme.colors.violet)};
                background-color: ${({ isDarkTheme }) =>
                    isDarkTheme ? theme.colors.backgroundTransparent : theme.colors.violetBackground};
                border-radius: ${theme.borderRadius.small};
                padding: 0 4px;
                svg {
                    position: relative;
                    bottom: 1.5px;
                }
                ${({ isDarkTheme }) =>
                    isDarkTheme &&
                    css`
                        svg path {
                            fill: ${theme.colors.grayScaleSecondary};
                        }
                    `}
            }
        }
    }

    a {
        text-decoration: none !important;
    }
`

const ThumbnailContainer = styled.div<{ isDarkTheme?: boolean }>`
    width: 100%;
    height: 200px;
    background-color: ${({ isDarkTheme }) =>
        isDarkTheme ? theme.colors.backgroundTransparent : theme.colors.violetBackground};
    display: flex;
    justify-content: center;
    padding-top: 25px;
    overflow: hidden;
    border: 1px solid ${({ isDarkTheme }) => (isDarkTheme ? theme.colors.transparent : theme.colors.violet)};
    border-radius: ${theme.borderRadius.content};
`

const KnowImage = styled.img`
    width: 400px;
    height: 600px;
    border-radius: ${theme.borderRadius.normal};
    object-fit: contain;
    object-position: top;
    box-shadow: rgba(255, 255, 255, 0.1) 0px 1px 1px 0px inset, rgba(50, 50, 93, 0.25) 0px 50px 100px -20px,
        rgba(0, 0, 0, 0.3) 0px 30px 60px -30px;
    @media (max-width: 1400px) {
        width: 300px;
    }
`

const EmptyPreview = styled.div`
    width: 400px;
    height: 200px;
    background: ${theme.colors.deepSeaBlue};
    border-radius: ${theme.borderRadius.large};
    display: grid;
    place-content: center;
    position: relative;
    @media (max-width: 1400px) {
        width: 300px;
    }
    svg {
        fill: white;
        width: 50px;
        height: 50px;
    }
`

const Description = styled(Body2)`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`

const Headline = styled(Body1)<{ isDarkTheme?: boolean }>`
    overflow: hidden;
    text-overflow: ellipsis;
    padding: 0;
    span {
        color: ${({ isDarkTheme }) => (isDarkTheme ? theme.colors.white : theme.colors.deepSeaBlue)};
    }
`

const loadingAreas = {
    know: 'knowContent',
}

interface Props {
    knowUuid: string
    highlight?: boolean
    mobileHighlight?: boolean
    hoverColor?: string
    startSnapScroll?: boolean
    know?: Know
    referrerScreen?: string
    disabledLinking?: boolean
    path?: string
    feed?: FeedEventOptions
    isDarkTheme?: boolean
    onClick?: (know?: Know) => void
    search?: SearchOptions
    is100PercentMatch?: boolean
}

const FeedKnowCard = (props: Props) => {
    const [know, setKnow] = useState<Know | null>(props.know ?? null)
    const { t: tCommon } = _useTranslation('common')
    const small = useMedia(deviceSize.sm)
    const router = useRouter()
    const referrerScreen = props.referrerScreen
    const { disabledLinking, path, feed, isDarkTheme, search, is100PercentMatch } = props

    const fetchData = useCallback(() => {
        if (props.know) return

        trackPromise(
            KnowService.get(props.knowUuid)
                .then((know) => {
                    setKnow(know)
                })
                .catch((error) => {
                    if (error instanceof ApiError) {
                        if (error.type === ErrorTypes.NotFound) {
                            toast.error(tCommon('common/knowNotFoundError'))
                        } else {
                            error.handleUnknownError(tCommon, 'know.get')
                        }
                    } else {
                        throw error
                    }
                }),
            loadingAreas.know
        )
    }, [props.know, props.knowUuid, tCommon])

    useEffect(() => {
        fetchData()
    }, [fetchData])

    if (!know) return null

    const knowPath = path ?? knowRoute(know.slug, { referrerScreen: referrerScreen })

    const attachTrackingDataOption = () => {
        props.onClick && props.onClick(know)
        feed && storeFeedOption(feed)
        search && storeSearchOption(search)
    }

    const redirectToKnow = () => {
        props.onClick && props.onClick(know)

        if (disabledLinking) return

        router.push(knowPath)

        attachTrackingDataOption()
    }

    const KnowWrapper = (props: { children: ReactNode }) =>
        isKnowNotIndexable(know) || disabledLinking ? (
            <div onClick={redirectToKnow}>{props.children}</div>
        ) : (
            <span onClick={attachTrackingDataOption}>
                <Link href={knowPath} passHref prefetch={false} legacyBehavior>
                    <a>{props.children}</a>
                </Link>
            </span>
        )

    const subject = know?.subjects?.length ? know.subjects[0] : null

    const showFlashcardsIcon = know.knowFormat === KnowFormat.Flashcards

    return (
        <KnowWrapper>
            {know ? <KnowImpressionTracking know={know} referrerScreen={referrerScreen} feed={props.feed} /> : null}
            <Container disableLinking={disabledLinking} isDarkTheme={isDarkTheme}>
                <LoadingArea area={loadingAreas.know}>
                    <ThumbnailContainer className="thumbnail" isDarkTheme={isDarkTheme}>
                        {know.thumbnailSmallUrl ? (
                            <KnowImage
                                src={know.thumbnailLargeUrl ?? know.thumbnailSmallUrl}
                                alt={`Know ${know.seoTitle ?? know.title} thumbnail`}
                                width={350}
                                height={600}
                                loading="lazy"
                            />
                        ) : (
                            <EmptyPreview>{showFlashcardsIcon ? <FlashcardInfo know={know} /> : <PDFIcon />}</EmptyPreview>
                        )}
                    </ThumbnailContainer>
                    {!small ? (
                        <div className="profile">
                            <ProfilePicture user={know.knower.user} size={64} />
                        </div>
                    ) : null}
                    <div className="bottom">
                        <div className="stats">
                            {is100PercentMatch ? (
                                <div className="matching-content-icon">
                                    <MatchingContentIcon />
                                    <ButtonText fontFamily="var(--font-inter)" fontWeight={600} color={theme.colors.white}>
                                        {tCommon('common/matching100Content')}
                                    </ButtonText>
                                </div>
                            ) : null}
                            <div className="icon">
                                <KnowLikes />
                                <ButtonText
                                    fontFamily="var(--font-inter)"
                                    fontWeight={600}
                                    color={isDarkTheme ? theme.colors.grayScaleSecondary : theme.colors.actionGray}
                                >
                                    {know.likes}
                                </ButtonText>
                            </div>
                            <div className="icon">
                                <KnowViews />
                                <ButtonText
                                    fontFamily="var(--font-inter)"
                                    fontWeight={600}
                                    color={isDarkTheme ? theme.colors.grayScaleSecondary : theme.colors.actionGray}
                                >
                                    {know.views}
                                </ButtonText>
                            </div>
                            <div className="icon">
                                <KnowComment />
                                <ButtonText
                                    fontFamily="var(--font-inter)"
                                    fontWeight={600}
                                    color={isDarkTheme ? theme.colors.grayScaleSecondary : theme.colors.actionGray}
                                >
                                    {know.commentsCount}
                                </ButtonText>
                            </div>
                            {showFlashcardsIcon ? (
                                <div className="icon">
                                    <KnowFlashcardCount />
                                    <ButtonText
                                        fontFamily="var(--font-inter)"
                                        fontWeight={600}
                                        color={isDarkTheme ? theme.colors.grayScaleSecondary : theme.colors.actionGray}
                                    >
                                        {know.flashcardCount}
                                    </ButtonText>
                                </div>
                            ) : null}
                        </div>

                        <Stack gutter={5}>
                            <Headline
                                isDarkTheme={isDarkTheme}
                                fontFamily="var(--font-inter)"
                                fontWeight={600}
                                color={isDarkTheme ? theme.colors.grayScaleSecondary : theme.colors.headlineDark}
                            >
                                {subject ? <span>{`${formatSubjectLabel(subject)} - `}</span> : null}
                                {know.seoTitle ?? know.title}
                            </Headline>

                            <Description
                                fontFamily="var(--font-inter)"
                                fontWeight={400}
                                color={isDarkTheme ? theme.colors.grayScaleSecondary : theme.colors.actionGray}
                            >
                                {know.seoDescription ?? know.description}
                            </Description>
                        </Stack>
                    </div>
                </LoadingArea>
            </Container>
        </KnowWrapper>
    )
}

export default FeedKnowCard
